import type { FC, ReactNode } from 'react';
import { ActionGroup } from 'src/components/buttons-and-actions/action-group';
import type { Size } from 'src/components/buttons-and-actions/button';
import { DropdownTrigger } from 'src/components/buttons-and-actions/dropdown-trigger';
import { flattenChildren } from 'src/utils/flatten-children';

type SplitActionsProps = {
  children: ReactNode;
  initialActive?: boolean;
  empty?: ReactNode;
  testId?: string;
  mode?: 'button-primary' | 'button-secondary';
  size?: Size;
};

/**
 * This component groups a dynamic amount of buttons and links. Only the first
 * action will be visible by default. The rest will be placed inside a dropdown.
 *
 * Nothing will be rendered, if there are no actions.
 */
export const SplitActions: FC<SplitActionsProps> = ({
  children,
  initialActive = false,
  empty = null,
  testId,
  mode,
  size,
}) => {
  const actions = flattenChildren(children);
  if (!actions.length) return empty;

  const [firstAction, ...remainingActions] = actions;

  return (
    <ActionGroup mode={mode} size={size}>
      {firstAction}

      {remainingActions.length ? (
        <DropdownTrigger
          aria-label="Show or Hide Additional Actions"
          data-testid={
            testId !== undefined ? `split-actions-toggle-${testId}` : undefined
          }
          initialOpen={initialActive}
        >
          {remainingActions}
        </DropdownTrigger>
      ) : null}
    </ActionGroup>
  );
};
